import React, {Fragment} from 'react'
import {findDOMNode} from 'react-dom'
import {CheckboxGroup} from 'common/styleguide/CheckboxGroup'
import {CTA} from 'common/styleguide/CTA'
import {Accordion, AccordionItem} from 'common/styleguide/Accordion'
import {OrderedMap, Set} from 'immutable'
import {Icon} from 'common/Icon'
import tippy from 'tippy.js'
import {tippyDefaults} from 'lib/tippyDefaults'
import {connect} from 'react-redux'
import {interfaceBase} from 'lib/styleguide/base.scss'
import {RangeInput} from 'common/styleguide/RangeInput'
import * as currentUserActions from 'lib/api/currentUser'
import {formatsForObjectType, fileDownloadURL, SMART_EXPORT_FILE_DOWNLOAD} from 'lib/exportFormats'

export const EXPORT_DIALOG = 'EXPORT_DIALOG'

const exportOpts = OrderedMap({
  turnNotificationDistance: 'Notify before turn',
  reduceTo: 'Reduce to 500 points',
  poiAsWpt: 'Include POI as waypoints',
  cuesAsWpt: 'Include cues as waypoints'
})

export class ExportFormat extends React.PureComponent {
  static defaultProps = {
    defaultTurnNotificationDistance: 30,
    defaultTurnNotificationChecked: true
  }

  constructor (props) {
    super(props)

    const defaultActiveOptions = this.userHasOptionsCapability() && this.props.defaultTurnNotificationChecked
      ? Set(['turnNotificationDistance'])
      : Set()

    this.state = {
      turnNotificationDistance: props.defaultTurnNotificationDistance, // todo sticky
      activeOptions: defaultActiveOptions,
      showTooltipFor: 'turnNotificationDistance'
    }
  }

  // todo handle club routes!
  userHasOptionsCapability = () => {
    const {troute} = this.props
    return (
      (troute.permissions && troute.permissions.customizeExportOptions) ||
      // needed for older RSP code that doesn't use V3Record
      troute._permissions.customize_export_options
    )
  }

  onChangeOptions = (activeOptions, event) => {
    if (!this.userHasOptionsCapability()) {
      return
    }
    this.setState({activeOptions})
  }

  onClickOption = (e) => {
    if (!this.userHasOptionsCapability()) {
      // otherwise, the user attempted to activate an option but
      // wasn't permitted to. Show the upgrade tooltip.
      const tippyNode = e.target.parentElement.querySelector('a')
      // make a best-effort to show the tooltip, but don't crash if
      // it's not possible (ie the user clicked the actual reference element,
      // or the tooltip didn't initialize, or there's some weird mount/unmount)
      tippyNode && tippyNode._tippy && tippyNode._tippy.show()
    }
  }

  onChangeTurnNotificationDistance = (turnNotificationDistance) => {
    this.setState({turnNotificationDistance})
  }

  fileDownloadURL = (format) => {
    const {activeOptions, turnNotificationDistance} = this.state
    const {troute, objectType} = this.props

    return fileDownloadURL(format, troute.id, objectType, activeOptions, troute.privacyCode, turnNotificationDistance)
  }

  // currently the only sticky option is turnNotificationDistance
  persistStickyOptions = (format) => {
    const {setPreference, userId, objectType} = this.props
    // not strictly needed since logged-out users can't activate turn notifications
    // but let's be defensive here anyways
    if (!userId) { return }
    const {turnNotificationDistance, activeOptions} = this.state
    if (format.supportedOptions.has('turnNotificationDistance')) {
      setPreference('route_export_advanced_turn_notification_checked', activeOptions.has('turnNotificationDistance'))
    }
    if (format.supportedOptions.has('turnNotificationDistance') && activeOptions.has('turnNotificationDistance')) {
      setPreference('route_export_advanced_turn_notification', turnNotificationDistance)
    }

    if (format.smartExportable) {
      const formatKey = formatsForObjectType(objectType).findKey((f) => f.name === format.name)
      setPreference('smart_export_state', SMART_EXPORT_FILE_DOWNLOAD)
      setPreference('smart_export_file_format', formatKey)
    }
  }

  render () {
    const {f} = this.props
    const {activeOptions, turnNotificationDistance} = this.state

    const visibleOpts = exportOpts.filter((v, k) => f.supportedOptions.has(k))
    const withUpsells = this.userHasOptionsCapability()
      ? visibleOpts
      : visibleOpts.map(label => <Fragment>{label} <UpgradeIcon /></Fragment>)

    return <Fragment>
      <p>{f.description}</p>
      <CheckboxGroup
        horizontal
        onClickOption={this.onClickOption}
        onChange={this.onChangeOptions}
        options={withUpsells} selected={activeOptions} />
      {f.supportedOptions.has('turnNotificationDistance') && activeOptions.has('turnNotificationDistance') && <form>
        <label>Turn Notification Distance: <strong>{turnNotificationDistance} meters</strong></label>
        <RangeInput
          type='range'
          value={turnNotificationDistance}
          onChange={this.onChangeTurnNotificationDistance}
          step={1} minValue={5} maxValue={100} style={{marginBottom: '20px'}} />
      </form>}
      <CTA margins={false} href={this.fileDownloadURL(f)} onClick={() => this.persistStickyOptions(f)}>Download .{f.ext} File</CTA>
    </Fragment>
  }
}

const mapStateToProps = (state) => ({
  defaultTurnNotificationDistance: state.currentUser.preferences.get('route_export_advanced_turn_notification'),
  defaultTurnNotificationChecked: state.currentUser.preferences.get('route_export_advanced_turn_notification_checked'),
  accountLevel: state.currentUser.accountLevel,
  userId: state.currentUser.id
})

const mapDispatchToProps = {
  setPreference: currentUserActions.setPreference
}

export const ExportFormatContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(ExportFormat)

export class ExportFormats extends React.PureComponent {
  render () {
    const {troute, objectType} = this.props

    return (
      <div className={interfaceBase}>
        <Accordion>
          {formatsForObjectType(objectType)
            .map(f => <AccordionItem defaultExpanded={f.defaultExpanded === objectType} title={`${f.name} (.${f.ext})`} key={f.name}>
              <ExportFormatContainer f={f} troute={troute} objectType={objectType} />
            </AccordionItem>).valueSeq()}
          <AccordionItem title='Map/Profile Images'>
            <p>Export images of the map and elevation profile</p>
            <CTA href={`/${objectType}s/full/${troute.id}.png`} download>Download Map Image (.png)</CTA>
            <CTA margins={false} href={`/${objectType}s/${troute.id}/elevation_profile.jpg${troute.privacyCode ? `?privacy_code=${troute.privacyCode}` : ''}`} download>Download Elevation Profile (.jpg)</CTA>
          </AccordionItem>
        </Accordion>
      </div>
    )
  }
}

class UpgradeIcon extends React.PureComponent {
  componentDidMount () {
    const node = findDOMNode(this)
    tippy(node, tippyDefaults)
    this._tippy = node._tippy
  }

  componentWillUnmount () {
    const node = findDOMNode(this)
    node._tippy && node._tippy.destroy()
  }

  render () {
    return (
      <a href='/choose_account' title='Upgrade your account to access this feature'><Icon i={'premium-arrow'} /></a>
    )
  }
}
